import { CustomRule, Filter, PreviewCustomRule } from '../models/filter-models';
import { flattenCosmeticCustomRulesInOrder, flattenCustomRulesInOrder } from '../common/custom-rules/custom-rule-groups-helper';
import { Rarity } from '../models/enums';
import { Tier, UniqueTierType } from '../models/item-data-models';
import { GameItem } from '../models/game-item.models';
import { SimulatorResult } from '../models/simulator-models';
import { ItemLibraryRecord } from './item-library.service';
import { RandomLootItem } from './random-loot.service';
import { DropPreviewRuleGroups, DropPreviewSection } from './drop-preview.models';
import { getGameItemExampleName } from './game-item-display.util';

const SIMULATION_BATCH_SIZE = 15;

export interface DropPreviewGenerationContext {
  filter: Filter;
  libraryItems: ItemLibraryRecord[];
  lootItems: RandomLootItem[];
  poe1: boolean;
  lookupItemClass: (baseType: string) => string | undefined;
  getFirstCurrencyForDropTier: (tier: Tier) => string | undefined;
  getFirstDivinationCardForDivinationCardTier: (tier: Tier) => string | undefined;
  getFirstUniqueForUniqueTier: (tier: UniqueTierType) => string;
  getFirstFavouriteRestrictedBossUniqueBaseType?: (excludeBaseTypes: ReadonlySet<string>) => string | undefined;
  getFirstFavouriteLowValueBossUniqueBaseType?: (excludeBaseTypes: ReadonlySet<string>) => string | undefined;
  getFirstOtherLowValueBossUniqueBaseType?: (excludeBaseTypes: ReadonlySet<string>) => string | undefined;
  simulate: (item: GameItem, cacheKey?: string) => SimulatorResult;
}

interface TierSimulationJob {
  cacheKey: string;
  item: GameItem;
}

export function emptyDropPreviewRuleGroups(): DropPreviewRuleGroups {
  return {
    itemLibrary: [],
    randomLoot: [],
    currencyDropTiers: [],
    divinationCardDropTiers: [],
    uniqueDropTiers: [],
    gold: [],
    customCosmeticRules: [],
    customRules: [],
  };
}

export async function buildDropPreviewRuleGroupsAsync(
  context: DropPreviewGenerationContext,
  isStale: () => boolean,
): Promise<DropPreviewRuleGroups | null> {
  if (isStale()) return null;

  const groups = emptyDropPreviewRuleGroups();
  const { filter, poe1 } = context;

  const itemLibrary = await simulateLibraryItems(context, isStale);
  if (itemLibrary === null) return null;
  groups.itemLibrary = itemLibrary;

  const randomLoot = await simulateLootItems(context, isStale);
  if (randomLoot === null) return null;
  groups.randomLoot = randomLoot;

  const currencyDropTiers = await simulateTierJobs(
    buildCurrencyTierJobs(filter, context),
    context,
    isStale,
  );
  if (currencyDropTiers === null) return null;
  groups.currencyDropTiers = currencyDropTiers;

  if (poe1) {
    const divinationCardDropTiers = await simulateTierJobs(
      buildDivinationCardTierJobs(filter, context),
      context,
      isStale,
    );
    if (divinationCardDropTiers === null) return null;
    groups.divinationCardDropTiers = divinationCardDropTiers;
  }

  const uniqueDropTiers = await simulateTierJobs(
    [
      ...buildUniqueTierJobs(filter, context),
      ...buildFavouriteEndgameBossUniqueJobs(filter, context),
    ],
    context,
    isStale,
  );
  if (uniqueDropTiers === null) return null;
  groups.uniqueDropTiers = uniqueDropTiers;

  const gold = await simulateTierJobs(
    buildGoldTierJobs(filter, context),
    context,
    isStale,
  );
  if (gold === null) return null;
  groups.gold = gold;

  groups.customCosmeticRules = flattenCosmeticCustomRulesInOrder(filter)
    .map(r => convertCustomRuleToPreviewRule(r));

  groups.customRules = flattenCustomRulesInOrder(filter)
    .map(r => convertCustomRuleToPreviewRule(r));

  return groups;
}

function buildCurrencyTierJobs(
  filter: Filter,
  context: DropPreviewGenerationContext,
): TierSimulationJob[] {
  const jobs: TierSimulationJob[] = [];

  for (const tierCosmetic of filter.tierCosmetics) {
    const baseType = context.getFirstCurrencyForDropTier(tierCosmetic.tier!);
    if (!baseType) continue;

    const item = buildCurrencyGameItem(baseType, context);
    if (!item) continue;

    jobs.push({
      cacheKey: `drop-preview-currency-${tierCosmetic.tier}`,
      item,
    });
  }

  return jobs;
}

function buildDivinationCardTierJobs(
  filter: Filter,
  context: DropPreviewGenerationContext,
): TierSimulationJob[] {
  const jobs: TierSimulationJob[] = [];

  for (const tierCosmetic of filter.divCardTierCosmetics) {
    const baseType = context.getFirstDivinationCardForDivinationCardTier(tierCosmetic.tier!);
    if (!baseType) continue;

    const item = buildDivinationCardGameItem(baseType, context);
    if (!item) continue;

    jobs.push({
      cacheKey: `drop-preview-divcard-${tierCosmetic.tier}`,
      item,
    });
  }

  return jobs;
}

function buildUniqueTierJobs(
  filter: Filter,
  context: DropPreviewGenerationContext,
): TierSimulationJob[] {
  const jobs: TierSimulationJob[] = [];

  for (const tierCosmetic of filter.uniqueTierCosmetics) {
    if (tierCosmetic.uniqueTier == null) continue;

    const baseType = context.getFirstUniqueForUniqueTier(tierCosmetic.uniqueTier);
    if (!baseType) continue;

    const item = buildUniqueGameItem(baseType, context);
    if (!item) continue;

    jobs.push({
      cacheKey: `drop-preview-unique-${tierCosmetic.uniqueTier}`,
      item,
    });
  }

  return jobs;
}

function buildFavouriteEndgameBossUniqueJobs(
  filter: Filter,
  context: DropPreviewGenerationContext,
): TierSimulationJob[] {
  const excludeBaseTypes = getExistingUniqueTierExampleBaseTypes(filter, context);
  const jobs: TierSimulationJob[] = [];

  if (!filter.favouriteEndgameContent.includes('All')) {
    const restrictedBaseType = context.getFirstFavouriteRestrictedBossUniqueBaseType?.(excludeBaseTypes);
    if (restrictedBaseType) {
      const item = buildUniqueGameItem(restrictedBaseType, context);
      if (item) {
        jobs.push({
          cacheKey: 'drop-preview-unique-restricted-favourite-bosses',
          item,
        });
        excludeBaseTypes.add(restrictedBaseType);
      }
    }

    const lowValueBaseType = context.getFirstFavouriteLowValueBossUniqueBaseType?.(excludeBaseTypes);
    if (lowValueBaseType) {
      const item = buildUniqueGameItem(lowValueBaseType, context);
      if (item) {
        jobs.push({
          cacheKey: 'drop-preview-unique-favourite-bosses',
          item,
        });
        excludeBaseTypes.add(lowValueBaseType);
      }
    }
  }

  const otherLowValueBaseType = context.getFirstOtherLowValueBossUniqueBaseType?.(excludeBaseTypes);
  if (otherLowValueBaseType) {
    const item = buildUniqueGameItem(otherLowValueBaseType, context);
    if (item) {
      jobs.push({
        cacheKey: 'drop-preview-unique-other-bosses',
        item,
      });
    }
  }

  return jobs;
}

function getExistingUniqueTierExampleBaseTypes(
  filter: Filter,
  context: DropPreviewGenerationContext,
): Set<string> {
  const baseTypes = new Set<string>();

  for (const tierCosmetic of filter.uniqueTierCosmetics) {
    if (tierCosmetic.uniqueTier == null) continue;

    const baseType = context.getFirstUniqueForUniqueTier(tierCosmetic.uniqueTier);
    if (baseType) baseTypes.add(baseType);
  }

  return baseTypes;
}

const LEVELING_GOLD_PREVIEW_STACK_SIZES = [1, 5, 10, 50, 100];

function buildGoldTierJobs(
  filter: Filter,
  context: DropPreviewGenerationContext,
): TierSimulationJob[] {
  const jobs: TierSimulationJob[] = [];

  if (filter.showLevelingGold) {
    for (const stackSize of LEVELING_GOLD_PREVIEW_STACK_SIZES) {
      jobs.push({
        cacheKey: `drop-preview-gold-leveling-${stackSize}`,
        item: buildGoldGameItem(stackSize, context),
      });
    }
    return jobs;
  }

  if (filter.hideGold && filter.hideGoldLowerThan) {
    jobs.push({
      cacheKey: 'drop-preview-gold-hidden',
      item: buildGoldGameItem(Math.max(1, filter.hideGoldLowerThan - 1), context),
    });
  }

  const normalStackSize = filter.hideGold && filter.hideGoldLowerThan ? filter.hideGoldLowerThan : 50;
  jobs.push({
    cacheKey: 'drop-preview-gold-normal',
    item: buildGoldGameItem(normalStackSize, context),
  });

  if (filter.showLargeGoldStacks && filter.showLargeGoldStacksMinStackSize) {
    jobs.push({
      cacheKey: 'drop-preview-gold-large',
      item: buildGoldGameItem(filter.showLargeGoldStacksMinStackSize, context),
    });
  }

  if (filter.showHugeGoldStacks && filter.showHugeGoldStacksMinStackSize) {
    jobs.push({
      cacheKey: 'drop-preview-gold-huge',
      item: buildGoldGameItem(filter.showHugeGoldStacksMinStackSize, context),
    });
  }

  return jobs;
}

function buildCurrencyGameItem(
  baseType: string,
  context: DropPreviewGenerationContext,
): GameItem | null {
  const itemClass = context.lookupItemClass(baseType) ?? 'Stackable Currency';
  return new GameItem({ itemClass, baseType, rarity: 'Currency' });
}

function buildDivinationCardGameItem(
  baseType: string,
  context: DropPreviewGenerationContext,
): GameItem | null {
  const itemClass = context.lookupItemClass(baseType) ?? 'Divination Cards';
  return new GameItem({ itemClass, baseType, rarity: Rarity.Normal });
}

function buildUniqueGameItem(
  baseType: string,
  context: DropPreviewGenerationContext,
): GameItem | null {
  const itemClass = context.lookupItemClass(baseType);
  if (!itemClass) return null;

  return new GameItem({
    itemClass,
    baseType,
    rarity: Rarity.Unique,
    identified: false,
  });
}

function buildGoldGameItem(
  stackSize: number,
  context: DropPreviewGenerationContext,
): GameItem {
  const props: Partial<GameItem> = {
    baseType: 'Gold',
    rarity: 'Currency',
    stackSize,
  };

  if (!context.poe1) {
    props.itemClass = 'Stackable Currency';
  }

  return new GameItem(props);
}

async function simulateTierJobs(
  jobs: TierSimulationJob[],
  context: DropPreviewGenerationContext,
  isStale: () => boolean,
): Promise<PreviewCustomRule[] | null> {
  const rules: PreviewCustomRule[] = [];

  for (let i = 0; i < jobs.length; i += SIMULATION_BATCH_SIZE) {
    if (isStale()) return null;
    await yieldToMain();
    if (isStale()) return null;

    const batch = jobs.slice(i, i + SIMULATION_BATCH_SIZE);
    for (const { cacheKey, item } of batch) {
      const sim = context.simulate(item, cacheKey);
      if (sim.displayOptions) {
        rules.push(simulatedItemToPreviewRule(sim, cacheKey));
      }
    }
  }

  return rules;
}

async function simulateLibraryItems(
  context: DropPreviewGenerationContext,
  isStale: () => boolean,
): Promise<PreviewCustomRule[] | null> {
  const rules: PreviewCustomRule[] = [];
  const items = context.libraryItems;

  for (let i = 0; i < items.length; i += SIMULATION_BATCH_SIZE) {
    if (isStale()) return null;
    await yieldToMain();
    if (isStale()) return null;

    const batch = items.slice(i, i + SIMULATION_BATCH_SIZE);
    for (const { hash, item, itemText } of batch) {
      const sim = context.simulate(item, hash);
      if (sim.displayOptions) {
        rules.push(gameItemToPreviewRule(sim, hash, 'library', itemText ?? null));
      }
    }
  }

  return rules;
}

async function simulateLootItems(
  context: DropPreviewGenerationContext,
  isStale: () => boolean,
): Promise<PreviewCustomRule[] | null> {
  const rules: PreviewCustomRule[] = [];
  const items = context.lootItems;

  for (let i = 0; i < items.length; i += SIMULATION_BATCH_SIZE) {
    if (isStale()) return null;
    await yieldToMain();
    if (isStale()) return null;

    const batch = items.slice(i, i + SIMULATION_BATCH_SIZE);
    for (const { hash, item, itemText } of batch) {
      const sim = context.simulate(item, hash);
      if (sim.displayOptions) {
        rules.push(gameItemToPreviewRule(sim, hash, 'randomLoot', itemText));
      }
    }
  }

  return rules;
}

function yieldToMain(): Promise<void> {
  return new Promise(resolve => requestAnimationFrame(() => resolve()));
}

function simulatedItemToPreviewRule(sim: SimulatorResult, cacheKey: string): PreviewCustomRule {
  const rule = gameItemToPreviewRule(sim, cacheKey, 'tier');
  rule.isSimulatedTierItem = true;
  return rule;
}

function gameItemToPreviewRule(
  sim: SimulatorResult,
  hash: string,
  source: 'library' | 'randomLoot' | 'tier' = 'library',
  itemText: string | null = null,
): PreviewCustomRule {
  const item = sim.item;
  const isRareOrIdentifiedUnique =
    item.rarity === Rarity.Rare || (item.rarity === Rarity.Unique && item.identified !== false);
  return new PreviewCustomRule({
    displayOptions: sim.displayOptions!,
    isItemLibraryItem: source === 'library',
    isRandomLootItem: source === 'randomLoot',
    itemLibraryHash: hash,
    itemLibraryText: (source === 'randomLoot' || source === 'library') ? itemText : undefined,
    librarySimulatorResult: sim,
    itemClass: item.itemClass,
    exampleName: getGameItemExampleName(item),
    rareName: isRareOrIdentifiedUnique ? item.name : undefined,
    unidentifiedItemTier: item.unidentifiedItemTier,
    rarity: item.rarity === 'Currency' ? undefined : item.rarity,
    sockets: item.socketGroup,
    influence: item.hasInfluence,
    memoryStrands: item.memoryStrands ? true : false,
    fractured: item.fracturedItem,
    synthesised: item.synthesisedItem,
    isCurrency: item.rarity === 'Currency',
    stackSize: item.stackSize,
    isWaystone: item.itemClass === 'Waystones' || item.itemClass === 'Maps' || item.itemClass === 'Tablets',
    isPoe1Gem: item.itemClass === 'Skill Gems' || item.itemClass === 'Support Gems',
    isDivinationCard: item.itemClass === 'Divination Cards',
    isQuestItem: item.itemClass === 'Quest Items' || item.itemClass === 'Instance Local Items',
  });
}

function convertCustomRuleToPreviewRule(r: CustomRule): PreviewCustomRule {
  const rule = new PreviewCustomRule(r);
  if (rule.itemClass === 'Divination Cards') rule.isDivinationCard = true;
  if (rule.itemClass === 'Skill Gems' || rule.itemClass === 'Support Gems') rule.isPoe1Gem = true;
  if (rule.advancedConditions?.hasInfluence && rule.advancedConditions?.influences?.length) {
    rule.influence = rule.advancedConditions?.influences![0] === 'All' ? 'Shaper' : rule.advancedConditions?.influences![0];
  }
  if (rule.advancedConditions?.fractured) rule.fractured = true;
  if (rule.advancedConditions?.synthesised) rule.synthesised = true;
  if (rule.advancedConditions?.veiled) rule.veiled = true;
  if (rule.advancedConditions?.socketGroup) rule.sockets = 'L' + rule.advancedConditions?.socketGroup;
  if (rule.advancedConditions?.minLinks) rule.sockets = 'L' + 'W'.repeat(rule.advancedConditions?.minLinks!);
  if (rule.advancedConditions?.minSockets) rule.sockets = 'W'.repeat(rule.advancedConditions?.minSockets!);
  return rule;
}

export const DROP_PREVIEW_SECTION_ORDER: DropPreviewSection[] = [
  'itemLibrary',
  'randomLoot',
  'currencyDropTiers',
  'divinationCardDropTiers',
  'uniqueDropTiers',
  'gold',
  'customCosmeticRules',
  'customRules',
];
